import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import useLocalStorage from '../hooks/useLocalStorage';
import useApi, { Api } from '../hooks/useApi';
import HttpError from '../errors/http_error';
import PKCE from 'js-pkce';

interface AuthContextType {
  user: any;
  storedToken: string | null;
  logout: () => void;
  login: (username: string, password: string) => Promise<void>;
  socialLoginInit: (provider: string) => Promise<void>;
  socialLogin: (url: string) => Promise<void>;
  loadUser: () => Promise<void>;
  setUser: (user: any) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  storedToken: null,
  logout: () => {},
  login: async () => {},
  socialLoginInit: async () => {},
  socialLogin: async () => {},
  loadUser: async () => {},
  setUser: () => {},
});

const useAuthContext = () => useContext(AuthContext);

/**
 * Hook to get an authenticated API instance.
 * Must be called at the top level of a React component (Rules of Hooks).
 */
const useAuthenticatedApi = (): Api => {
  const { storedToken } = useAuthContext();
  console.log('useAuthenticatedApi token:', storedToken ? storedToken.substring(0, 15) + '...' : 'NULL');
  return new Api(storedToken || '');
};

/**
 * Legacy wrapper — calls useAuthenticatedApi under the hood.
 * Kept for backward compat; callers should migrate to useAuthenticatedApi.
 */
const getApi = (): Api => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAuthenticatedApi();
};

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [storedToken, setStoredToken] = useLocalStorage<string | null>('pap:session', null);
  const [user, setUser] = useLocalStorage<any>('pap:user', null);
  const [, setSessionUser] = useState<any>(null);

  useEffect(() => {
    loadUser();
  }, [storedToken]);

  const logout = () => {
    setStoredToken(null);
    setSessionUser(null);
    setUser(null);
  };

  const origin = window.location.origin;
  const pkceConfig = {
    client_id: import.meta.env.VITE_API_CLIENT_ID,
    redirect_uri: `${origin}${import.meta.env.VITE_OAUTH_REDIRECT_URI}`,
    authorization_endpoint: `${origin}${import.meta.env.VITE_OAUTH_AUTHORIZATION_ENDPOINT}`,
    token_endpoint: `${origin}${import.meta.env.VITE_OAUTH_TOKEN_ENDPOINT}`,
    requested_scopes: 'All'
  };
  console.log('PKCE config:', JSON.stringify(pkceConfig));
  const pkce = new PKCE(pkceConfig);

  const socialLoginInit = async (provider: string) => {
    const url = pkce.authorizeUrl({ provider });
    console.log('PKCE authorizeUrl:', url);
    window.location.href = url;
  };

  const socialLogin = async (url: string) => {
    try {
      const resp = await pkce.exchangeForAccessToken(url);
      console.log('PKCE exchange response:', JSON.stringify(resp));
      const token = resp.access_token;
      if (token) {
        console.log('Setting token:', token.substring(0, 15) + '...');
        setStoredToken(token);
      } else {
        console.error('No access_token in response:', resp);
      }
    } catch (err) {
      console.error('PKCE exchange error:', err);
    }
  };

  const login = async (username: string, password: string) => {
    const response = await fetch('/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: import.meta.env.VITE_API_CLIENT_ID,
        grant_type: 'password',
        username,
        password
      })
    });
    if (!response.ok) {
      const error = await response.json();
      error.status = response.status;
      throw new HttpError(error);
    }

    const data = await response.json();
    if (data.access_token) {
      setStoredToken(data.access_token);
    }
  };

  const loadUser = async () => {
    if (storedToken) {
      const api = new Api(storedToken);
      try {
        const userData = await api.get('/api/v1/users/me');
        setUser(userData);
        setSessionUser(userData);
      } catch (ex: any) {
        console.error('loadUser failed:', ex);
        if (ex?.response?.status === 401 || ex?.status === 401) {
          logout();
        }
        // Don't logout on non-401 errors (500s, network issues, etc.)
      }
    }
  };

  console.log('AuthProvider rendering, socialLoginInit is real:', socialLoginInit.toString().substring(0, 50));
  return (
    <AuthContext.Provider value={{ user, loadUser, setUser, storedToken, logout, login, socialLogin, socialLoginInit }}>
      {children}
    </AuthContext.Provider>
  );
};

export { useApi, getApi, useAuthenticatedApi, useAuthContext, AuthProvider };
